// Estilos da barra da direita.
//
// Ficam num arquivo próprio, e não em `layouts.ts`, porque a barra deixou de
// ser um bloco de `style={{...}}` inline (eram ~40 objetos literais dentro do
// RightBar.tsx) e passou a ter componentes com nome. Um arquivo por área é o
// que o resto do repo já faz em `styles/components/`.
import styled from "styled-components";
import { ate, ALVO_DE_TOQUE } from "../global/breakpoints";
import { ESTRUTURA, CENTRAL } from "../global/tokens";

/*
 * ESTA BARRA É SEMPRE PAPEL, NOS DOIS TEMAS — e por isso as cores de texto aqui
 * NÃO vêm do `theme`.
 *
 * A barra da direita fica sobre o ladrilho amarelo (o `RightSidebarContainer` é
 * #fee97b em claro e escuro, porque é a moldura do ícone), e os cartões dela
 * sao lavados de branco. Uma superfície clara não muda com o tema, então o
 * texto dela tambem nao pode mudar: puxar theme.colors.label colocaria o
 * cinza-claro do tema escuro (#AFAFAF) sobre um cartão branco.
 *
 * Vale a regra do papel do sistema de design: carvão para texto, carvão-2 para
 * apoio, tinta para ação.
 */
const TEXTO = ESTRUTURA.carvao;
const APOIO = ESTRUTURA.carvao2;

export const Cabecalho = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  padding: var(--sp-12) var(--sp-15);
  background-color: #fee97b;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
`;

export const LinhaDoTitulo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-8);
`;

export const Titulo = styled.h1`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: var(--fs-lg);
  /* Tinta, e nao theme.colors.selection: no tema escuro a selecao e o proprio
     amarelo do ladrilho, e amarelo sobre a barra amarela desaparece. */
  color: ${CENTRAL.tinta};
  text-shadow: -0.5px -0.5px 0 #000, 0.5px -0.5px 0 #000, -0.5px 0.5px 0 #000,
    1px 1px 0 #000;
  /* Nome de loja é longo. Duas linhas e depois reticências: melhor que a linha
     única que cortava "Loja Number One ..." já no terceiro caractere. */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  line-height: 1.2;
`;

export const BotaoDoCabecalho = styled.button`
  display: grid;
  place-items: center;
  min-width: ${ALVO_DE_TOQUE};
  min-height: ${ALVO_DE_TOQUE};
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${TEXTO};
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

export const LinhaDaOrganizacao = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  margin-top: var(--sp-6);
  font-size: var(--fs-2xs);
  color: ${APOIO};
`;

export const Bolinha = styled.span<{ $estado: "online" | "warning" | "offline" }>`
  width: calc(var(--u) * 8);
  height: calc(var(--u) * 8);
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ $estado }) =>
    $estado === "online" ? "#1F9D55" : $estado === "warning" ? "#D69E2E" : "#C53030"};
`;

export const Abas = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background-color: #fee97b;
`;

export const Aba = styled.button<{ $ativa?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-2);
  min-height: ${ALVO_DE_TOQUE};
  padding: var(--sp-8) var(--sp-4);
  border: none;
  border-bottom: 3px solid
    ${({ $ativa }) => ($ativa ? CENTRAL.tinta : "transparent")};
  background: transparent;
  color: ${({ $ativa }) => ($ativa ? TEXTO : APOIO)};
  font-size: var(--fs-3xs);
  font-weight: ${({ $ativa }) => ($ativa ? 700 : 500)};
  cursor: pointer;

  .material-icons {
    font-size: var(--fs-lg);
  }
`;

export const Corpo = styled.div`
  padding: var(--sp-12);
  display: flex;
  flex-direction: column;
  gap: var(--sp-12);
`;

export const Cartao = styled.div`
  padding: var(--sp-12);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.55);
`;

export const TituloDoCartao = styled.h2`
  margin: 0 0 var(--sp-8);
  font-size: var(--fs-xs);
  font-weight: 700;
  color: ${TEXTO};
`;

export const Endereco = styled.p`
  margin: 0 0 var(--sp-10);
  font-size: var(--fs-2xs);
  line-height: 1.35;
  color: ${APOIO};
`;

export const Acoes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-8);
`;

export const BotaoDeAcao = styled.button<{ $whatsapp?: boolean }>`
  flex: 1 1 calc(var(--u) * 110);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-6);
  min-height: ${ALVO_DE_TOQUE};
  padding: var(--sp-6) var(--sp-10);
  border: none;
  border-radius: 8px;
  /* O verde do WhatsApp era #128C4A, e com branco por cima dava 4,31:1 —
     medido, e um fio abaixo dos 4,5:1. #128848 e' o mesmo verde escurecido o
     bastante para passar (4,52:1). O #2E7D32 do outro botao ja' dava 5,13:1. */
  background: ${({ $whatsapp }) => ($whatsapp ? "#128848" : "#2E7D32")};
  color: #fff;
  font-size: var(--fs-2xs);
  font-weight: 600;
  cursor: pointer;
  white-space: normal;

  .material-icons {
    font-size: var(--fs-base);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const GradeDeNumeros = styled.div`
  display: grid;
  /* Dois por linha na barra de 300; um só quando ela vira gaveta estreita. */
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 110), 1fr));
  gap: var(--sp-8);
`;

export const Numero = styled.div`
  padding: var(--sp-10);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.75);
  text-align: center;
`;

export const RotuloDoNumero = styled.div`
  font-size: var(--fs-3xs);
  color: ${APOIO};
  margin-bottom: var(--sp-2);
`;

export const ValorDoNumero = styled.div<{ $tom?: "verde" | "azul" | "ambar" }>`
  font-size: var(--fs-md);
  font-weight: 700;
  line-height: 1.15;
  overflow-wrap: anywhere;
  color: ${({ $tom }) =>
    $tom === "verde"
      ? "#2E7D32"
      : $tom === "azul"
      ? "#1565C0"
      : $tom === "ambar"
      ? "#A96500"
      : TEXTO};
`;

export const ListaDeUnidades = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.9);
`;

export const GrupoDaCompany = styled.div`
  padding: var(--sp-8) var(--sp-12);
  background: rgba(0, 0, 0, 0.07);
  font-size: var(--fs-2xs);
  font-weight: 700;
  color: ${TEXTO};
`;

export const ItemDeUnidade = styled.button<{ $selecionada?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  width: 100%;
  min-height: ${ALVO_DE_TOQUE};
  padding: var(--sp-10) var(--sp-12);
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: ${({ $selecionada }) =>
    $selecionada ? "rgba(254, 233, 123, 0.55)" : "transparent"};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: rgba(254, 233, 123, 0.35);
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const NomeDaUnidade = styled.div`
  font-size: var(--fs-2xs);
  font-weight: 600;
  color: ${TEXTO};
  line-height: 1.2;
`;

export const DetalheDaUnidade = styled.div`
  font-size: var(--fs-3xs);
  color: ${APOIO};
  line-height: 1.2;
`;

export const Aviso = styled.div<{ $tom?: "alerta" | "info" }>`
  display: flex;
  gap: var(--sp-8);
  padding: var(--sp-10);
  border-left: 3px solid
    ${({ $tom }) => ($tom === "alerta" ? "#C53030" : "#A96500")};
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.7);
  font-size: var(--fs-3xs);
  line-height: 1.4;
  color: ${APOIO};

  .material-icons {
    font-size: var(--fs-base);
    color: ${({ $tom }) => ($tom === "alerta" ? "#C53030" : "#A96500")};
  }
`;

export const CodigoDoAviso = styled.strong`
  display: block;
  margin-bottom: var(--sp-2);
  color: ${TEXTO};
  font-size: var(--fs-3xs);
  letter-spacing: 0.02em;
`;

export const Vazio = styled.div`
  padding: var(--sp-16) var(--sp-12);
  text-align: center;
  font-size: var(--fs-2xs);
  line-height: 1.4;
  color: ${APOIO};
`;

export const Rodape = styled.div`
  padding: var(--sp-10) var(--sp-12) var(--sp-20);
  font-size: var(--fs-3xs);
  line-height: 1.4;
  color: ${APOIO};

  ${ate.tablet} {
    padding-bottom: var(--sp-32);
  }
`;
